import "@/App.css";
import { useState, useEffect } from "react";
import EmailSender from "./components/EmailSender";
import InboxAssistant from "./components/InboxAssistant";
import Compose from "./components/Compose";
import Login from "./components/Login";
import DeliveryHistory from "./components/DeliveryHistory";
import { Toaster } from "./components/ui/sonner";
import { Send, Inbox, LogOut, Loader2, MailCheck, PenLine } from "lucide-react";
import { apiFetch, getToken, clearToken } from "./lib/api";
import { PendingSendProvider } from "./context/PendingSendContext";
import { UndoSendBanner } from "./components/UndoSendBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [page, setPage] = useState("inbox");
  // undefined = checking session, null = logged out, object = authenticated
  const [authUser, setAuthUser] = useState(undefined);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthUser(null);
      return;
    }
    apiFetch(`${BACKEND_URL}/api/auth/me`)
      .then(async (res) => {
        if (res.ok) setAuthUser(await res.json());
        else setAuthUser(null);
      })
      .catch(() => setAuthUser(null));
  }, []);

  useEffect(() => {
    const onUnauth = () => setAuthUser(null);
    window.addEventListener("ps-unauthorized", onUnauth);
    return () => window.removeEventListener("ps-unauthorized", onUnauth);
  }, []);

  const logout = () => {
    clearToken();
    setAuthUser(null);
    setPage("inbox");
  };

  const pillClass = (active) =>
    `flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
      active
        ? "bg-white text-primary shadow-sm"
        : "text-muted-foreground hover:text-foreground"
    }`;

  let content;
  if (authUser === undefined) {
    content = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  } else if (!authUser) {
    content = <Login onLogin={setAuthUser} />;
  } else {
    content = (
      <div className="min-h-screen bg-background">
        {/* Top Navigation */}
        <nav className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur-md px-4 sm:px-6 md:px-10 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/pure-souls-logo.svg"
              alt="Pure Souls Pet Aquamation"
              className="h-9 sm:h-11 w-auto"
            />
            <span className="hidden sm:inline text-sm font-medium text-muted-foreground border-l border-border pl-3">
              Email Hub
            </span>
          </div>

          <div className="flex gap-1 bg-[#F0EFEA] rounded-full p-1 flex-shrink-0">
            <button
              onClick={() => setPage("inbox")}
              data-testid="nav-inbox"
              className={pillClass(page === "inbox")}
            >
              <Inbox className="w-4 h-4" />
              <span className="hidden sm:inline">AI Inbox</span>
            </button>
            <button
              onClick={() => setPage("compose")}
              data-testid="nav-compose"
              className={pillClass(page === "compose")}
            >
              <PenLine className="w-4 h-4" />
              <span className="hidden sm:inline">Compose</span>
            </button>
            <button
              onClick={() => setPage("campaigns")}
              data-testid="nav-campaigns"
              className={pillClass(page === "campaigns")}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Campaigns</span>
            </button>
            <button
              onClick={() => setPage("history")}
              data-testid="nav-delivery-history"
              className={pillClass(page === "history")}
            >
              <MailCheck className="w-4 h-4" />
              <span className="hidden lg:inline">Sent</span>
            </button>
          </div>

          <button
            onClick={logout}
            data-testid="logout-btn"
            title="Sign out"
            className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden md:inline">Sign out</span>
          </button>
        </nav>

        {/* Page Content */}
        {page === "campaigns" ? <EmailSender /> : page === "compose" ? <Compose /> : page === "history" ? <DeliveryHistory /> : <InboxAssistant />}
      </div>
    );
  }

  return (
    <PendingSendProvider enabled={Boolean(authUser)}>
      {content}
      <UndoSendBanner />
      <Toaster
        position="top-right"
        offset={{ top: 72, right: 16 }}
        mobileOffset={{ top: 72, left: 16, right: 16 }}
      />
    </PendingSendProvider>
  );
}

export default App;
